import { useState, useEffect, useCallback } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { v4 as uuidv4 } from 'uuid'
import clsx from 'clsx'
import Decimal from 'decimal.js'
import PopupContainer from '@/components/PopupContainer'
import { marketMapState, markPriceMapState } from '@/recoil/Market'
import { closePositionPopupState, positionMapValue } from '@/recoil/Position'
import { reduceOrderMapValue } from '@/recoil/OrderList'
import { popupNotificationListState } from '@/recoil/Notification'
import fetcher from '@/api/fetcher'
import { ORDER_TYPE_MAP, ORDER_SIDE, ORDER_INPUT_MAP } from '@/constants/order'
import { BROKER_ID } from '@/constants/config'
import { isValidNumber } from '@/utils/numberFormat'
import { getErrorMessage } from '@/utils/order'
import './ClosePositionPopup.css'

export default function ClosePositionPopup() {
  const [{ show, marketSymbol }, setClosePositionPopup] = useRecoilState(
    closePositionPopupState
  )
  const [marketMap] = useRecoilState(marketMapState)
  const [markPriceMap] = useRecoilState(markPriceMapState)
  const positionMap = useRecoilValue(positionMapValue)
  const reduceOrderMap = useRecoilValue(reduceOrderMapValue)
  const setPopupNotification = useSetRecoilState(popupNotificationListState)
  const [isLoading, setIsLoading] = useState(false)
  const [orderType, setOrderType] = useState(ORDER_TYPE_MAP.limit)
  const [orderSize, setOrderSize] = useState('')
  const [orderPrice, setOrderPrice] = useState('')
  const [orderSizeError, setOrderSizeError] = useState('')
  const [orderPriceError, setOrderPriceError] = useState('')

  const isActive = show && marketSymbol !== ''
  const marketInfo = marketMap[marketSymbol] || {}
  const markPrice = Decimal(markPriceMap[marketSymbol] || 0)
    .toDecimalPlaces(marketInfo.priceDecimal, Decimal.ROUND_DOWN)
    .toFixed()
  const position = isActive ? positionMap[marketSymbol] || {} : {}
  const positionSize = Decimal(position.position_qty || 0).toNumber()
  const isLongPosition = Decimal(positionSize).gt(0)
  const hasPosition = !Decimal(positionSize).eq(0)
  const totalReduceOnlyOrderSize =
    hasPosition && reduceOrderMap[marketSymbol]
      ? reduceOrderMap[marketSymbol].size
      : 0
  const remainPositionSize = Decimal(positionSize)
    .abs()
    .sub(totalReduceOnlyOrderSize)
    .toNumber()
  const isMarketOrder = orderType === ORDER_TYPE_MAP.market

  const hidePopupCallback = useCallback(() => {
    setClosePositionPopup({
      show: false,
      marketSymbol: ''
    })
    setOrderType(ORDER_TYPE_MAP.limit)
    setOrderSize('')
    setOrderPrice('')
    setOrderSizeError('')
    setOrderPriceError('')
    setIsLoading(false)
  }, [setClosePositionPopup])

  useEffect(() => {
    if (!hasPosition || Decimal(remainPositionSize).lte(0)) {
      hidePopupCallback()
    }
  }, [hasPosition, remainPositionSize, hidePopupCallback])

  useEffect(() => {
    if (isMarketOrder) {
      setOrderPrice('')
      setOrderPriceError('')
    }
  }, [isMarketOrder])

  const onOrderSizeChange = v => {
    const value = String(v).replaceAll(',', '')
    if (!isValidNumber(value)) {
      return
    }
    const { base_min, base_max, base_tick } = marketInfo
    setOrderSize(value)
    setOrderSizeError(
      value === ''
        ? ''
        : getErrorMessage(ORDER_INPUT_MAP.size, value, {
            min: base_min,
            max: Decimal.min(base_max, remainPositionSize).toNumber(),
            tick: base_tick
          })
    )
  }
  const onOrderPriceChange = v => {
    const value = String(v).replaceAll(',', '')
    if (!isValidNumber(value)) {
      return
    }
    const { quote_min, quote_max, quote_tick } = marketInfo
    setOrderPrice(value)
    setOrderPriceError(
      value === ''
        ? ''
        : getErrorMessage(ORDER_INPUT_MAP.price, value, {
            min: quote_min,
            max: quote_max,
            tick: quote_tick
          })
    )
  }

  const onPercentageClick = percentage => {
    const { sizeDecimal, base_min } = marketInfo
    const size = Decimal.max(
      Decimal(remainPositionSize)
        .mul(percentage)
        .toFixed(sizeDecimal, Decimal.ROUND_DOWN),
      base_min
    ).toNumber()
    onOrderSizeChange(size)
  }

  const orderPriceValid =
    (isMarketOrder || orderPrice !== '') && orderPriceError === ''
  const orderSizeValid =
    orderSize !== '' &&
    Decimal(orderSize).lte(remainPositionSize) &&
    orderSizeError === ''
  const confirmButtonDisabled =
    isLoading || !hasPosition || !orderPriceValid || !orderSizeValid

  const onConfirmClick = () => {
    if (confirmButtonDisabled) {
      return
    }
    setIsLoading(true)
    const data = {
      client_order_id: uuidv4().replaceAll('-', '_'),
      broker_id: BROKER_ID,
      symbol: marketSymbol,
      order_type: orderType,
      side: isLongPosition ? ORDER_SIDE.sell : ORDER_SIDE.buy,
      order_quantity: Number(orderSize),
      reduce_only: true
    }

    if (orderType === ORDER_TYPE_MAP.limit) {
      data.order_price = Number(orderPrice)
    }

    fetcher
      .createOrder(data)
      .then(() => {
        setOrderSize('')
        setOrderPrice('')
        hidePopupCallback()
      })
      .catch(e => {
        const errorCode = e.response?.data?.code
        const errorMessage = e.response?.data?.message
        errorMessage &&
          setPopupNotification({
            notification: {
              key: Date.now(),
              status: 'error',
              code: errorCode,
              message: errorMessage
            }
          })
      })
      .finally(() => setIsLoading(false))
  }

  const [, baseCurrency] = marketSymbol.split('_')
  const marketName = `${baseCurrency}-PERP`

  return (
    <PopupContainer
      className="close-position-popup"
      show={show}
      hidePopupCallback={hidePopupCallback}
    >
      <div className="popup-title">Close the {marketName} Position</div>
      <div className="close-position-order-form">
        <div className="order-data-block">
          <div className="order-data-label">Qty.</div>
          <div className="order-input-wrap">
            <input
              className={clsx('order-data-input', { error: orderSizeError })}
              value={orderSize}
              onChange={e => onOrderSizeChange(e.target.value)}
            />
            {orderSizeError && (
              <div className="error-message">{orderSizeError}</div>
            )}
          </div>
          <div className="data-selector">
            <div
              className="data-selector-button noselect"
              onClick={() => onPercentageClick(0.25)}
            >
              25%
            </div>
            <div
              className="data-selector-button noselect"
              onClick={() => onPercentageClick(0.5)}
            >
              50%
            </div>
            <div
              className="data-selector-button noselect"
              onClick={() => onPercentageClick(0.75)}
            >
              75%
            </div>
            <div
              className="data-selector-button noselect"
              onClick={() => onPercentageClick(1)}
            >
              100%
            </div>
          </div>
        </div>
        <div className="order-data-block">
          <div className="order-data-label">Price</div>
          <div className="order-input-wrap">
            <input
              className={clsx('order-data-input', { error: orderPriceError })}
              placeholder={markPrice}
              value={orderPrice}
              onChange={e => onOrderPriceChange(e.target.value)}
              disabled={orderType === ORDER_TYPE_MAP.market}
            />
            {orderPriceError && (
              <div className="error-message">{orderPriceError}</div>
            )}
          </div>
          <div className="data-selector">
            <div
              className={clsx('data-selector-button noselect', {
                active: isMarketOrder
              })}
              onClick={() => setOrderType(ORDER_TYPE_MAP.market)}
            >
              Market
            </div>
            <div
              className={clsx('data-selector-button noselect', {
                active: !isMarketOrder
              })}
              onClick={() => setOrderType(ORDER_TYPE_MAP.limit)}
            >
              Limit
            </div>
          </div>
        </div>
      </div>
      <div
        className={clsx('main-action-button noselect', {
          disabled: confirmButtonDisabled
        })}
        onClick={onConfirmClick}
      >
        Confirm
      </div>
    </PopupContainer>
  )
}
